export type ExplicitProfile = {
  worldviewBackground?: string;
  familiarityWithIslam?: string;
  motive?: string;
  reasoningPreference?: string;
  difficultyComfort?: string;
  explorationStyle?: 'open' | 'guided';
  preferredModuleDepth?: 'simple' | 'full' | 'study';
  adaptRecommendations?: boolean;
};

export type ClusterKey =
  | 'foundations'
  | 'quran'
  | 'prophet'
  | 'theology'
  | 'ethics_society'
  | 'comparative'
  | 'difficult';

export type ExploreModuleLike = {
  slug: string;
  title: string;
  summary: string | null;
  topic_tags_json: string;
  sensitivity: number;
  topic_family: string | null;
  site_section: string | null;
  estimated_read_minutes?: number;
};

export type JourneyRowLike = { slug: string; title: string };

function norm(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().trim();
}

function safeParseTags(json: string | null | undefined): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(String).filter(Boolean);
  } catch {
    return [];
  }
}

function hasAnyTag(tags: string[], needles: string[]): boolean {
  const t = tags.map((x) => x.toLowerCase());
  return needles.some((n) => t.some((tt) => tt === n.toLowerCase() || tt.includes(n.toLowerCase())));
}

export function scoreClusterForProfile(clusterKey: ClusterKey, explicit: ExplicitProfile): number {
  const motive = norm(explicit.motive);
  const background = norm(explicit.worldviewBackground);
  const familiarity = norm(explicit.familiarityWithIslam);
  const depthComfort = norm(explicit.difficultyComfort);

  let score = 0;

  // Motive-first.
  if (motive === 'curiosity') {
    if (clusterKey === 'foundations') score += 24;
    if (clusterKey === 'quran') score += 16;
    if (clusterKey === 'theology') score += 10;
  }
  if (motive === 'comparison') {
    if (clusterKey === 'comparative') score += 36;
    if (clusterKey === 'theology') score += 14;
    if (clusterKey === 'ethics_society') score += 10;
  }
  if (motive === 'concern') {
    if (clusterKey === 'ethics_society') score += 18;
    if (clusterKey === 'difficult') score += 22;
    if (clusterKey === 'foundations') score += 10;
  }
  if (motive === 'doubt') {
    if (clusterKey === 'difficult') score += 30;
    if (clusterKey === 'foundations') score += 14;
    if (clusterKey === 'quran') score += 10;
  }
  if (motive === 'spiritual_search') {
    if (clusterKey === 'quran') score += 26;
    if (clusterKey === 'prophet') score += 10;
    if (clusterKey === 'foundations') score += 10;
  }
  if (motive === 'specific_issue') {
    if (clusterKey === 'difficult') score += 28;
    if (clusterKey === 'ethics_society') score += 10;
    if (clusterKey === 'theology') score += 8;
  }

  // Background bias.
  if (background === 'christian') {
    if (clusterKey === 'comparative') score += 30;
    if (clusterKey === 'theology') score += 12;
    if (clusterKey === 'quran') score += 10;
  }
  if (background === 'atheist' || background === 'agnostic') {
    if (clusterKey === 'difficult') score += 22;
    if (clusterKey === 'comparative') score += 18;
    if (clusterKey === 'theology') score += 16;
  }
  if (background === 'hindu') {
    if (clusterKey === 'comparative') score += 22;
    if (clusterKey === 'theology') score += 10;
  }
  if (background === 'muslim') {
    if (clusterKey === 'foundations') score += 12;
    if (clusterKey === 'quran') score += 16;
    if (clusterKey === 'prophet') score += 10;
    if (clusterKey === 'ethics_society') score += 10;
  }

  // Familiarity.
  if (familiarity === 'totally_new') {
    if (clusterKey === 'foundations') score += 34;
    if (clusterKey === 'quran') score += 20;
    if (clusterKey === 'theology') score += 12;
    if (clusterKey === 'difficult') score -= 10;
    if (clusterKey === 'comparative') score -= 6;
  }
  if (familiarity === 'researching_deeply') {
    if (clusterKey === 'difficult') score += 28;
    if (clusterKey === 'comparative') score += 18;
    if (clusterKey === 'ethics_society') score += 8;
  }
  // `know_basics` / `studied_little`: near-neutral.

  // Depth comfort bias.
  if (depthComfort === 'overview_first') {
    if (clusterKey === 'foundations') score += 22;
    if (clusterKey === 'quran') score += 14;
    if (clusterKey === 'difficult') score -= 16;
  }
  if (depthComfort === 'direct_deep_dive') {
    if (clusterKey === 'difficult') score += 30;
    if (clusterKey === 'comparative') score += 10;
  }

  return score;
}

export function orderClustersFromProfile(baseOrder: ClusterKey[], explicit: ExplicitProfile): ClusterKey[] {
  const scored = baseOrder.map((k, idx) => ({
    k,
    idx,
    score: scoreClusterForProfile(k, explicit),
  }));
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.idx - b.idx;
  });
  return scored.map((x) => x.k);
}

export function scoreModuleForProfile(m: ExploreModuleLike, clusterKey: ClusterKey, explicit: ExplicitProfile): number {
  // Base depth preference from "depth comfort".
  const depthComfort = norm(explicit.difficultyComfort);
  const motive = norm(explicit.motive);

  const read = m.estimated_read_minutes ?? 10;
  const summaryLen = m.summary?.length ?? 0;

  const depthMetric = read * 1.2 + summaryLen * 0.015;
  const shortBias = 1 / (1 + depthMetric / 220);
  const longBias = depthMetric / (depthMetric + 180);

  let score = 0;

  if (depthComfort === 'overview_first') score += shortBias * 22;
  else if (depthComfort === 'direct_deep_dive') score += longBias * 22;
  else score += (shortBias + longBias) * 10;

  // Cluster-specific editorial fit.
  if (clusterKey === 'foundations') score += (1 - m.sensitivity) * 26;
  if (clusterKey === 'difficult') score += m.sensitivity * 28;
  if (clusterKey === 'comparative') score += hasAnyTag(safeParseTags(m.topic_tags_json), ['comparative', 'christianity', 'atheism']) ? 16 : 6;

  const tags = safeParseTags(m.topic_tags_json).map((t) => t.toLowerCase());

  if (clusterKey === 'quran') score += hasAnyTag(tags, ['quran', 'revelation', 'scripture', 'tafsir']) ? 18 : 6;
  if (clusterKey === 'prophet') score += hasAnyTag(tags, ['prophet', 'prophethood', 'muhammad']) ? 16 : 6;
  if (clusterKey === 'theology') score += hasAnyTag(tags, ['theology', 'tawhid', 'attributes', 'belief']) ? 16 : 6;
  if (clusterKey === 'ethics_society') score += hasAnyTag(tags, ['ethics', 'justice', 'law', 'society', 'mercy']) ? 16 : 6;

  // Motive fine-tune.
  if (motive === 'doubt' && clusterKey === 'difficult') score += 10;
  if (motive === 'comparison' && clusterKey === 'comparative') score += 8;

  // Small stabilizer: prefer a slightly more descriptively authored module.
  score += Math.min(8, summaryLen / 180);

  return score;
}

// Back-compat alias (used by ExplorePage).
export const moduleScoreForProfile = scoreModuleForProfile;

export function scoreJourneyForProfile(journeySlug: string, explicit: ExplicitProfile): number {
  const motive = norm(explicit.motive);
  const background = norm(explicit.worldviewBackground);
  const familiarity = norm(explicit.familiarityWithIslam);
  const depthComfort = norm(explicit.difficultyComfort);

  const slug = journeySlug.toLowerCase();
  let score = 0;

  const bump = (target: string, amount: number) => {
    if (slug === target) score += amount;
  };

  // Motive routing.
  if (motive === 'curiosity') {
    bump('exploring-islam-for-the-first-time', 34);
    bump('foundations-of-faith', 24);
    bump('the-quran-journey', 12);
  }
  if (motive === 'comparison') {
    bump('christian-questions-about-islam', 38);
    bump('reason-science-and-human-nature', 18);
    bump('foundations-of-faith', 12);
  }
  if (motive === 'concern') {
    bump('returning-after-doubt', 22);
    bump('difficult-questions-about-islam', 26);
    bump('foundations-of-faith', 10);
  }
  if (motive === 'doubt') {
    bump('returning-after-doubt', 42);
    bump('difficult-questions-about-islam', 24);
    bump('the-quran-journey', 10);
  }
  if (motive === 'spiritual_search') {
    bump('worship-and-the-pillars', 38);
    bump('the-quran-journey', 20);
    bump('foundations-of-faith', 12);
  }
  if (motive === 'specific_issue') {
    bump('difficult-questions-about-islam', 40);
    bump('returning-after-doubt', 18);
  }

  // Background bias.
  if (background === 'christian') bump('christian-questions-about-islam', 42);
  if (background === 'atheist' || background === 'agnostic') bump('reason-science-and-human-nature', 34);
  if (background === 'muslim') {
    bump('the-quran-journey', 18);
    bump('worship-and-the-pillars', 18);
    bump('foundations-of-faith', 12);
  }

  // Familiarity.
  if (familiarity === 'totally_new') {
    bump('exploring-islam-for-the-first-time', 44);
    bump('foundations-of-faith', 26);
    bump('the-quran-journey', 14);
    bump('difficult-questions-about-islam', -10);
  }
  if (familiarity === 'researching_deeply') {
    bump('difficult-questions-about-islam', 26);
    bump('reason-science-and-human-nature', 24);
    bump('christian-questions-about-islam', 16);
  }

  // Depth comfort.
  if (depthComfort === 'overview_first') {
    bump('exploring-islam-for-the-first-time', 26);
    bump('foundations-of-faith', 18);
    bump('difficult-questions-about-islam', -14);
  }
  if (depthComfort === 'direct_deep_dive') {
    bump('difficult-questions-about-islam', 34);
    bump('returning-after-doubt', 14);
    bump('exploring-islam-for-the-first-time', -10);
  }

  return score;
}

export function orderJourneysFromProfile(
  rows: JourneyRowLike[],
  narrativeOrder: string[],
  explicit: ExplicitProfile
): JourneyRowLike[] {
  const orderIndex = new Map<string, number>(narrativeOrder.map((slug, idx) => [slug, idx]));
  const scored = rows.map((r) => {
    const base = orderIndex.has(r.slug) ? (orderIndex.get(r.slug) as number) : Number.POSITIVE_INFINITY;
    return { r, base, score: scoreJourneyForProfile(r.slug, explicit) };
  });
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.base !== b.base) return a.base - b.base;
    return a.r.title.localeCompare(b.r.title);
  });
  return scored.map((x) => x.r);
}

export function profileHint(explicit: ExplicitProfile): string | null {
  const motive = norm(explicit.motive);
  const background = norm(explicit.worldviewBackground);
  if (motive === 'comparison' || background === 'christian') return 'for comparison & careful clarity';
  if (motive === 'doubt' || motive === 'concern') return 'for doubts, tension, and steady grounding';
  if (motive === 'spiritual_search') return 'for spiritual search & reverent structure';
  if (motive === 'specific_issue') return 'for a direct path through the hardest themes';
  if (motive === 'curiosity') return 'for thoughtful entry & foundational understanding';
  return null;
}

